import { component, computed, observable, subscribe } from 'knockout-decorators'

import { Offer, PaymentMode, PaymentModeBalance } from '@tixa/schema'
import CreditCardImages from '~/images/cards.svg?raw'

import source from './template.html?raw'
import { Language } from '~/utils'
import { CartState } from '../state'

import './styles.css'

@component('cart-payment', source)
export class CartPayment {
  private creditCardImages = CreditCardImages
  private cartState: CartState = CartState.get()

  @observable public termsPdfUrl = Language.getInstance().translations.termspdf
  constructor() {
    subscribe<PaymentMode[]>(
      () => this.paymentModes,
      (newModes) => {
        if (
          !newModes.includes(this.cartState.paymentMode) &&
          newModes.length > 0
        ) {
          this.cartState.paymentMode = newModes[0]
        }
        if (newModes.length === 0) {
          this.cartState.paymentMode = PaymentMode.free
        }
      }
    )
  }

  @computed get paymentModes(): PaymentMode[] {
    if (!this.cartState.content) {
      return []
    }
    return this.cartState.content.payment || []
  }

  @computed get hasPayment(): boolean {
    return this.paymentModes.length > 0
  }

  @computed get balances(): PaymentModeBalance[] {
    if (!this.cartState.content) {
      return []
    }
    return this.cartState.content.balance || []
  }

  @computed get allowPartialBalanceUse(): boolean {
    if (!this.cartState.content) {
      return false
    }
    return this.cartState.content.allowPartialBalanceUse || false
  }

  private selectPaymentMode(mode: PaymentMode) {
    this.cartState.paymentMode = mode
  }

  @computed get paymentModeHasBalance() {
    const balance = this.balances.find(
      (b) => b.type === this.cartState.paymentMode
    )
    return balance !== undefined
  }

  @computed get paymentModeBalance() {
    const balance = this.balances.find(
      (b) => b.type === this.cartState.paymentMode
    )
    if (!balance) {
      return ''
    }
    return Language.formatCurrency(balance.value, balance.currency)
  }

  @computed get paymentModeBalanceN() {
    const balance = this.balances.find(
      (b) => b.type === this.cartState.paymentMode
    )
    if (!balance) {
      return -1
    }
    return balance.value
  }

  @computed get isPaymentBalanceSufficient() {
    const balance = this.balances.find(
      (b) => b.type === this.cartState.paymentMode
    )
    if (!balance) {
      return true
    }
    if (this.allowPartialBalanceUse) {
      return true
    }
    return balance.value > this.finalPrice
  }

  @computed get hasPartialBalanceFunding() {
    return (
      this.cartState.paymentMode === PaymentMode.accountcredit &&
      this.allowPartialBalanceUse &&
      this.paymentModeBalanceN > 0
    )
  }

  @computed get activeOffers(): Offer[] {
    const result = [
      ...this.cartState.offers.filter(
        (offer) => offer.eligibleQuantity.value > 0 && offer.price > 0
      ),
      ...this.cartState.offers.filter(
        (offer) =>
          this.cartState.chosenOptionalOffers.includes(offer['@id']) &&
          offer.price > 0
      ),
    ]

    const shippingOffer = this.cartState.offers.find(
      (offer) => offer.sku === this.cartState.selectedShippingSku
    )

    if (shippingOffer) {
      result.push({
        ...shippingOffer,
        eligibleQuantity: {
          ...shippingOffer.eligibleQuantity,
          value: 1,
        },
      })
    }
    return result
  }

  @computed get discountOffers(): Offer[] {
    const discount = this.cartState.offers.filter(
      (offer) => offer.category === 'discount'
    )
    return discount
  }

  @computed get finalPrice(): number {
    const price = this.activeOffers.reduce(
      (prev, curr) =>
        prev + Math.max(1, curr.eligibleQuantity.value) * curr.price,
      0
    )

    const totalDiscount = Math.abs(
      this.discountOffers.reduce(
        (prev, curr) =>
          prev + Math.max(1, curr.eligibleQuantity.value) * curr.price,
        0
      )
    )

    const totalTouristTax = this.cartState.content?.touristTaxAmount?.total || 0

    const finalPrice =
      price +
      totalTouristTax -
      totalDiscount -
      (this.allowPartialBalanceUse ? this.paymentModeBalanceN : 0)

    return finalPrice
  }

  @computed get needToPay(): boolean {
    return this.finalPrice > 0
  }
  @computed get bottomLine(): string {
    if (this.cartState.offers.length === 0) {
      return ''
    }
    return Language.formatCurrency(
      this.finalPrice,
      this.cartState.offers[0].priceCurrency
    )
  }

  @computed get isSubmittingOrder(): boolean {
    return this.cartState.isSubmittingOrder
  }
}
